/**
 * TuneUpPlannerView (PLANNER-02 A — "Fill the week", Seth 2026-09-08:
 * "All 3 options would be great actually. continue.")
 *
 * The tech-day is the batch. The page is the week of tech-days: what's
 * already on each tech's Board (ESC + Pegasus), the cities they're in, and
 * the room left under the season cap. Pick a light day → "Fill this day"
 * proposes the nearest still-owed checks around the existing calls, lays
 * the whole day out in drive order and gives every stop the arrival window
 * its place on the route implies (a promised "Burnsville by noon" is a
 * deadline the route bends around). Booked ✓ is one click: the day, the
 * tech and the window are already set — it creates the real Pegasus
 * dispatch (Board + confirmation text) and flips the season visit.
 *
 * PLANNER-01's saved day plan is still the record underneath (grown to know
 * its tech, date, origin and each stop's window) — nothing built twice.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, Download, ExternalLink, Hand, Home, Inbox, Lasso,
  MapPin, MapPinOff, Moon, Package, Phone, Printer, RefreshCw, Route as RouteIcon, Settings2, Sparkles, Trash2, Undo2, X,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { InfoTip } from "@/components/InfoTip";
import { TechAvatar } from "@/components/TechAvatar";
import { toastError } from "@/components/ui/mutationToast";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { usableCoords, routeMiles, driveMinutes, type LatLng } from "@/lib/route";
import { sequenceDay, pickCandidates, fmtHour, insideLoop, effectiveWindow, DAY_START, type FixedCall, type SequencedStop } from "@/lib/fillDay";
import { haversineMiles } from "@/lib/route";
import { centroid } from "@/lib/batch";
import { supabase } from "@/lib/supabase";
import { DispatchMap, type MapPoint } from "@/components/DispatchMap";
import { useSeasonVisits, useSeasonFinish, useSeasonPace, currentSeasonKey, seasonFromKey, defaultFinish, summarize, pace, isOutstanding, type SeasonVisit } from "./season";
import { DispatchDetailModal } from "@/features/board/DispatchDetailModal";
import { PegasusJobDetailModal } from "@/features/board/PegasusJobDetail";
import { usePendingTicketById } from "@/features/board/queue";
import type { Dispatch } from "@/lib/types";
import { useSegments, useAllAgreements, type AgrReportRow } from "@/features/reports/api";
import { matchesAll, type FilterRow } from "@/features/reports/agreementFilter";
import {
  useTechDayLoad, useTechDayCalls, usePlannerRules, useSetPlannerRule, roomFor, weekMonday, weekDays, fmtDay, firstName, isoDate,
  DEFAULT_RULES, type TechDayCell, type TechDayCall, type PlannerRules, type OfficeLocation,
} from "./techdays";
import {
  useVisitDays, useCreateDay, useUpdateDay, useDeleteDay,
  useAddStops, useUpdateStop, useRemoveStop, useReorderStops,
  useBookStop, useUnbookStop, useProposeNow, useDecideProposal,
  STOP_STATUS_LABEL, type VisitDay, type VisitDayStop, type NewStop,
} from "./planner";

const WINDOWS = ["8-12", "10-2", "12-4", "3-7"] as const;

// ----------------------------------------------------------------------------
// The pool: who can go on a day. The season's outstanding visits (default),
// or a saved segment's members when the page is opened from one.
// ----------------------------------------------------------------------------
interface PoolMember extends LatLng {
  key: string;            // visit id, or custno|locno for segment members
  visit_id: string | null;
  custno: string;
  locno: string;
  name: string;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  context: string;
  pickedDate: string | null;   // the date they asked for online
  pickedWindow: string | null;
  renewal: boolean;
  atRisk: boolean;
  invited: boolean;
  placedDayId: string | null;
}

function poolFromSeason(rows: SeasonVisit[]): PoolMember[] {
  const out: PoolMember[] = [];
  const byProp = new Map<string, PoolMember>();
  for (const r of rows) {
    if (!isOutstanding(r.status) || r.status === "snoozed") continue;
    if (!usableCoords(r.latitude, r.longitude)) continue;
    // A two-system home has two visits this season but is ONE stop.
    const propKey = `${r.custno}|${r.locno ?? "00001"}`;
    const prev = byProp.get(propKey);
    if (prev) {
      prev.context = prev.context.includes("systems") ? prev.context : `${prev.context} · 2 systems`;
      if (r.status === "picked" && !prev.pickedDate) { prev.pickedDate = r.booked_date; prev.pickedWindow = (r.reason ?? "").replace(/^picked\s*/, "") || null; }
      prev.renewal = prev.renewal || r.renewal_at_this_visit; prev.atRisk = prev.atRisk || r.at_risk;
      continue;
    }
    const m: PoolMember = {
      key: r.id, visit_id: r.id, custno: r.custno, locno: r.locno ?? "00001",
      name: displayCustomerName(null, r.customer_fullname) || r.custno,
      address: r.address, city: r.city, zip: r.zip, phone: r.phone, email: r.email,
      lat: r.latitude as number, lng: r.longitude as number,
      context: [r.sequence_label, r.renewal_at_this_visit ? "renewal at this visit" : null, r.at_risk ? "at risk" : null].filter(Boolean).join(" · "),
      pickedDate: r.status === "picked" ? r.booked_date : null,
      pickedWindow: r.status === "picked" ? (r.reason ?? "").replace(/^picked\s*/, "") || null : null,
      renewal: r.renewal_at_this_visit, atRisk: r.at_risk, invited: r.status === "invited",
      placedDayId: r.placed_day_id,
    };
    byProp.set(propKey, m);
    out.push(m);
  }
  return out;
}

function useSegmentPool(segId: string | null) {
  const { data: segments } = useSegments("agreement");
  const seg = segId ? (segments ?? []).find((s) => s.id === segId) ?? null : null;
  const { data: allAgr } = useAllAgreements(Boolean(segId));
  const members = useMemo(() => {
    if (!seg || !allAgr) return [] as { custno: string; locno: string; name: string; sub: string }[];
    return allAgr
      .filter((r: AgrReportRow) => matchesAll(r, (seg.definition as { filters?: FilterRow[] }).filters ?? []))
      .map((r) => ({ custno: r.custno, locno: r.locno, name: displayCustomerName(null, r.customer) || r.custno, sub: `${r.plan} · $${Math.round(r.value).toLocaleString()}` }));
  }, [seg, allAgr]);
  const custnos = useMemo(() => Array.from(new Set(members.map((m) => m.custno))).sort(), [members]);
  const loc = useQuery({
    queryKey: ["planner_locations", custnos],
    enabled: custnos.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const map = new Map<string, { lat: number | null; lng: number | null; address: string | null; zip: string | null; phone: string | null; email: string | null; city: string | null }>();
      for (let i = 0; i < custnos.length; i += 400) {
        const { data, error } = await supabase.from("mirror_location").select("custno, locno, add1, city, zip, phone1, email, latitude, longitude").in("custno", custnos.slice(i, i + 400));
        if (error) throw error;
        for (const l of (data ?? []) as Array<{ custno: string; locno: string; add1: string | null; city: string | null; zip: string | null; phone1: string | null; email: string | null; latitude: number | null; longitude: number | null }>) {
          const ok = usableCoords(l.latitude, l.longitude);
          map.set(`${l.custno}|${l.locno}`, { lat: ok ? l.latitude : null, lng: ok ? l.longitude : null, address: l.add1, city: l.city, zip: l.zip, phone: l.phone1, email: l.email });
        }
      }
      return map;
    },
  });
  const pool = useMemo((): PoolMember[] => {
    const out: PoolMember[] = [];
    const seen = new Set<string>();
    for (const m of members) {
      const k = `${m.custno}|${m.locno}`;
      if (seen.has(k)) continue; seen.add(k);
      const l = loc.data?.get(k);
      if (!l || typeof l.lat !== "number" || typeof l.lng !== "number") continue;
      out.push({ key: k, visit_id: null, custno: m.custno, locno: m.locno, name: m.name, address: l.address, city: l.city, zip: l.zip, phone: l.phone, email: l.email, lat: l.lat, lng: l.lng, context: m.sub, pickedDate: null, pickedWindow: null, renewal: false, atRisk: false, invited: false, placedDayId: null });
    }
    return out;
  }, [members, loc.data]);
  return { seg, pool, loading: Boolean(segId) && (!allAgr || (custnos.length > 0 && loc.isLoading)) };
}

// ----------------------------------------------------------------------------
// Sequencing on the client: existing calls (fixed) + this day's stops (free,
// or fixed when the dispatcher locked a window). Deterministic, so the panel,
// the map and the saved windows agree.
// ----------------------------------------------------------------------------
interface DayRow {
  seq: SequencedStop;
  call?: TechDayCall;
  stop?: VisitDayStop;
  /** A "Pick Up Part" ticket: where the tech gets the part, and whether it starts his day. */
  pickup?: Pickup;
}

// Seth 2026-09-09: a tech with a part ticket starts (or breaks) his day where
// the part is — the office in Eagan unless the ticket says otherwise — so the
// route starts there, and an open day is anchored from home (ESC's technician
// record) rather than nowhere.
interface Pickup { call: TechDayCall; when: "start" | "mid"; where: LatLng; label: string }
const PICKUP_CUSTNO = "0079253";
const PICKUP_HOURS = 0.25;
const MID_DAY_WINDOWS = new Set(["10-2", "11-1", "12-4", "3-7"]);

/** "Sedgwick office · Eagan" when the point is the office; else the ticket's own address. */
function pickupPlace(where: LatLng, office: OfficeLocation | null, address?: string | null, city?: string | null): string {
  if (office && haversineMiles(where, office) <= 0.5) return office.label;
  const a = [address, city].filter(Boolean).join(", ");
  return a || office?.label || "the office";
}

function pickupOf(c: TechDayCall, office: OfficeLocation | null): Pickup | null {
  if (c.custno !== PICKUP_CUSTNO) return null;
  const where: LatLng | null = usableCoords(c.lat, c.lng) ? { lat: c.lat as number, lng: c.lng as number } : office ? { lat: office.lat, lng: office.lng } : null;
  if (!where) return null;
  const win = effectiveWindow(c.window_code, c.notes);
  const when: "start" | "mid" = win && MID_DAY_WINDOWS.has(win) ? "mid" : "start";
  return { call: c, when, where, label: `Part pickup · ${pickupPlace(where, office, c.address, c.city)}` };
}

/**
 * The Board's fixed calls for a tech-day, plus where and when the day starts:
 * a start-of-day part pickup moves the start to the office at 8:15; a mid-day
 * pickup is a 15-minute fixed stop the route bends around.
 */
function dayFixed(calls: TechDayCall[], office: OfficeLocation | null, skipPegasus?: Set<string>) {
  const fixed: FixedCall[] = [];
  const pickups = new Map<string, Pickup>();
  let start: Pickup | null = null;
  for (const c of calls) {
    const pu = pickupOf(c, office);
    if (pu) {
      pickups.set(`call:${c.id}`, pu);
      if (pu.when === "start" && !start) start = pu;
      else fixed.push({ id: `call:${c.id}`, lat: pu.where.lat, lng: pu.where.lng, window: pu.when === "start" ? "8-12" : effectiveWindow(c.window_code, c.notes), hours: PICKUP_HOURS });
      continue;
    }
    if (c.pseudo || !usableCoords(c.lat, c.lng)) continue;
    if (c.source === "pegasus" && skipPegasus?.has(c.id)) continue; // the booked stop row carries it
    fixed.push({ id: `call:${c.id}`, lat: c.lat as number, lng: c.lng as number, window: effectiveWindow(c.window_code, c.notes) });
  }
  return { fixed, pickups, start, startAt: start ? start.where : null, startHour: start ? DAY_START + PICKUP_HOURS : DAY_START };
}

/** Where a row sits on the map (a pickup without coordinates sits at the office). */
const rowPos = (r: DayRow): LatLng => (r.pickup ? r.pickup.where : { lat: (r.call?.lat ?? r.stop?.lat) as number, lng: (r.call?.lng ?? r.stop?.lng) as number });

function buildRows(calls: TechDayCall[], stops: VisitDayStop[], anchor: LatLng | null, office: OfficeLocation | null = null): { rows: DayRow[]; unmapped: VisitDayStop[] } {
  const byId = new Map<string, TechDayCall | VisitDayStop>();
  const ownDispatches = new Set(stops.map((s) => s.dispatch_id).filter(Boolean) as string[]);
  const { fixed, pickups, start, startAt, startHour } = dayFixed(calls, office, ownDispatches);
  for (const c of calls) byId.set(`call:${c.id}`, c);
  const free: { id: string; lat: number; lng: number }[] = [];
  const unmapped: VisitDayStop[] = [];
  for (const s of stops) {
    if (s.status === "skipped") continue;
    if (!usableCoords(s.lat, s.lng)) { unmapped.push(s); continue; }
    const id = `stop:${s.id}`;
    byId.set(id, s);
    if (s.status === "booked" || s.window_locked) fixed.push({ id, lat: s.lat as number, lng: s.lng as number, window: s.status === "booked" ? (s.booked_window ?? s.arrival_window) : s.arrival_window });
    else free.push({ id, lat: s.lat as number, lng: s.lng as number });
  }
  // The tech starts at a start-of-day part pickup (the office), else at the first promised call, else at the anchor.
  const seq = sequenceDay(fixed, free, startAt ?? (fixed.length > 0 ? null : anchor), startHour);
  const rows: DayRow[] = seq.map((q) => {
    const src = byId.get(q.id);
    return q.id.startsWith("call:") ? { seq: q, call: src as TechDayCall, pickup: pickups.get(q.id) } : { seq: q, stop: src as VisitDayStop };
  });
  if (start) rows.unshift({ seq: { id: `call:${start.call.id}`, kind: "fixed", eta: DAY_START, window: "first thing", late: false, milesFromPrev: 0 }, call: start.call, pickup: start });
  return { rows, unmapped };
}

/** A stop's name for display (ESC "Last,First" flips; Pegasus names pass through). */
const stopName = (s: { name: string | null; custno: string }) => (s.name ? displayCustomerName(null, s.name) : s.custno);

/** Members → stops for a tech-day, in drive order with the route's windows (shared by Fill, the loop and the proposals). */
function stopsForDay(calls: TechDayCall[], members: PoolMember[], anchor: LatLng | null, date: string, office: OfficeLocation | null = null): NewStop[] {
  const { fixed, startAt, startHour } = dayFixed(calls, office);
  const seq = sequenceDay(fixed, members.map((m) => ({ id: m.key, lat: m.lat, lng: m.lng })), startAt ?? (fixed.length > 0 ? null : anchor), startHour);
  const byKey = new Map(members.map((m) => [m.key, m]));
  const out: NewStop[] = [];
  for (const q of seq) {
    if (q.kind !== "free") continue;
    const m = byKey.get(q.id); if (!m) continue;
    out.push({
      custno: m.custno, locno: m.locno, name: m.name, address: m.address, city: m.city, zip: m.zip, phone: m.phone, email: m.email, lat: m.lat, lng: m.lng,
      context: m.pickedDate === date ? `asked for ${fmtDay(date, { weekday: "short" })}${m.pickedWindow ? ` · ${m.pickedWindow}` : ""}` : m.context,
      visit_id: m.visit_id, arrival_window: q.window, eta_hour: Math.round(q.eta * 4) / 4,
      priority: m.pickedDate === date ? "picked" : m.atRisk ? "at_risk" : m.renewal ? "renewal" : null,
    });
  }
  return out;
}

type AnchorKind = "calls" | "pickup" | "home" | "none";
interface DayAnchor { at: LatLng | null; kind: AnchorKind; label: string }
/** Where a tech-day starts from: the calls already on the Board → a start-of-day part pickup (the office) → home (the Rules override, else ESC's technician record). */
function dayAnchor(cell: TechDayCell, rules: PlannerRules): DayAnchor {
  if (typeof cell.anchor_lat === "number" && typeof cell.anchor_lng === "number") return { at: { lat: cell.anchor_lat, lng: cell.anchor_lng }, kind: "calls", label: cell.cities ?? "his calls" };
  if (cell.pickup_when === "start" && typeof cell.pickup_lat === "number" && typeof cell.pickup_lng === "number") {
    const at = { lat: cell.pickup_lat, lng: cell.pickup_lng };
    return { at, kind: "pickup", label: pickupPlace(at, rules.office) };
  }
  const o = rules.homeAreas[cell.empno];
  if (o) return { at: { lat: o.lat, lng: o.lng }, kind: "home", label: o.label };
  if (typeof cell.home_lat === "number" && typeof cell.home_lng === "number") return { at: { lat: cell.home_lat, lng: cell.home_lng }, kind: "home", label: cell.home_label ?? "home" };
  return { at: null, kind: "none", label: "" };
}

function csvEscape(v: string | number | null | undefined): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename: string, rows: (string | number | null)[][]) {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ----------------------------------------------------------------------------
// Seth 2026-09-09: "interact with the tickets and view them while in this
// mode" — any Board call opens its ticket (ESC dispatch / Pegasus job) right
// here; any stop opens the customer in a new tab, so the planner keeps its
// place.
// ----------------------------------------------------------------------------
interface CallRef { source: "esc" | "pegasus"; id: string }
const PlannerViewCtx = createContext<{ openCall: (c: CallRef) => void; openCustomer: (custno: string) => void }>({ openCall: () => undefined, openCustomer: () => undefined });
const usePlannerView = () => useContext(PlannerViewCtx);
const openCustomerTab = (custno: string) => window.open(`/customers/${encodeURIComponent(custno)}`, "_blank", "noopener");

function CallViewer({ call, onClose }: { call: CallRef | null; onClose: () => void }) {
  const esc = useQuery({
    queryKey: ["planner_call_esc", call?.id],
    enabled: call?.source === "esc",
    queryFn: async (): Promise<Dispatch | null> => {
      const { data, error } = await supabase.from("v_dispatch_full").select("*").eq("dispatch", call!.id).order("counter", { ascending: true, nullsFirst: false }).limit(1);
      if (error) throw error;
      return ((data ?? []) as Dispatch[])[0] ?? null;
    },
  });
  const peg = usePendingTicketById(call?.source === "pegasus" ? call.id : null);
  if (!call) return null;
  if (call.source === "esc") return <DispatchDetailModal dispatch={esc.data ?? null} onClose={onClose} />;
  return <PegasusJobDetailModal job={peg.data ?? null} onClose={onClose} />;
}

// ============================================================================
export function TuneUpPlannerView() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const segId = params.get("seg");

  // ---- Season numbers (the strip) ----
  const seasonKey = currentSeasonKey();
  const sn = seasonFromKey(seasonKey);
  const { data: seasonRows, isLoading: seasonLoading } = useSeasonVisits(seasonKey);
  const totals = useMemo(() => summarize(seasonRows ?? []), [seasonRows]);
  const { data: finish } = useSeasonFinish(seasonKey);
  const finishISO = finish ?? defaultFinish(sn);
  const remaining = totals.owed - totals.done;
  const pc = pace(remaining, finishISO);
  const { data: lastSeason } = useSeasonPace(seasonKey);
  const weeksLeft = pc.weeksLeft;

  // ---- The pool ----
  const seasonPool = useMemo(() => poolFromSeason(seasonRows ?? []), [seasonRows]);
  const segment = useSegmentPool(segId);
  const pool = segId ? segment.pool : seasonPool;
  const poolLoading = segId ? segment.loading : seasonLoading;

  // ---- The week ----
  const [monday, setMonday] = useState(() => weekMonday());
  const days = useMemo(() => weekDays(monday), [monday]);
  const { data: cells, isLoading: cellsLoading, refetch: refetchCells } = useTechDayLoad(days[0], days[4]);
  const { data: rulesData } = usePlannerRules();
  const rules = rulesData ?? DEFAULT_RULES;
  const techs = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of cells ?? []) if (!seen.has(c.empno)) seen.set(c.empno, c.tech_name);
    return [...seen.entries()].map(([empno, name]) => ({ empno, name })).sort((a, b) => firstName(a.name).localeCompare(firstName(b.name)));
  }, [cells]);
  const cellFor = (empno: string, day: string) => (cells ?? []).find((c) => c.empno === empno && c.day === day) ?? null;
  const pickedByDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells ?? []) m.set(c.day, c.picked);
    return m;
  }, [cells]);

  // ---- Selection ----
  const [sel, setSel] = useState<{ empno: string; day: string } | null>(null);
  const selCell = sel ? cellFor(sel.empno, sel.day) : null;
  const selTech = sel ? techs.find((t) => t.empno === sel.empno) ?? null : null;

  const daysQ = useVisitDays();
  const selPlan: VisitDay | null = useMemo(() => {
    if (!sel) return null;
    return (daysQ.data ?? []).find((d) => d.tech_empno === sel.empno && d.plan_date === sel.day && d.status !== "done") ?? null;
  }, [daysQ.data, sel]);
  const placedKeys = useMemo(() => {
    const s = new Set<string>();
    for (const d of daysQ.data ?? []) if (d.status !== "done") for (const st of d.stops) { if (st.visit_id) s.add(st.visit_id); s.add(`${st.custno}|${st.locno}`); }
    return s;
  }, [daysQ.data]);
  const unplaced = useMemo(() => pool.filter((p) => !placedKeys.has(p.key) && !(p.visit_id && placedKeys.has(p.visit_id)) && !p.placedDayId), [pool, placedKeys]);

  // ?open=rules — Settings → Planner Rules lands straight on the rules.
  const [rulesOpen, setRulesOpen] = useState(() => params.get("open") === "rules");
  const homes = useMemo(() => techs.map((t) => ({ empno: t.empno, name: t.name, label: rules.homeAreas[t.empno]?.label ?? (cells ?? []).find((c) => c.empno === t.empno)?.home_label ?? null })), [techs, cells, rules]);
  const [viewCall, setViewCall] = useState<CallRef | null>(null);
  const plannerView = useMemo(() => ({ openCall: (c: CallRef) => setViewCall(c), openCustomer: openCustomerTab }), []);
  const [mode, setMode] = useState<"week" | "map" | "proposals">(params.get("view") === "map" ? "map" : params.get("view") === "proposals" ? "proposals" : "week");
  const proposedCount = useMemo(() => (daysQ.data ?? []).filter((d) => d.status === "proposed").length, [daysQ.data]);
  // Map mode → placing a batch on a tech-day lands you back on that day.
  const placeOn = (empno: string, day: string) => {
    setMonday(weekMonday(new Date(`${day}T00:00:00`)));
    setSel({ empno, day });
    setMode("week");
  };

  // ---- Header lines ----
  const today = isoDate(new Date());
  const roomThisWeek = useMemo(() => (cells ?? []).filter((c) => c.day >= today).reduce((s, c) => s + roomFor(c, rules), 0), [cells, rules, today]);
  const openTechDays = useMemo(() => (cells ?? []).filter((c) => !c.off && c.day >= today).length, [cells, today]);
  const proposedThisWeek = useMemo(() => (cells ?? []).reduce((s, c) => s + Math.max(0, (c.plan_stops ?? 0) - (c.plan_booked ?? 0)), 0), [cells]);

  return (
    <PlannerViewCtx.Provider value={plannerView}>
    <div className="flex h-full flex-col overflow-y-auto p-6">
      {/* Header */}
      <div className="mb-3 flex items-start gap-3">
        <button type="button" onClick={() => navigate("/agreements")} className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
          <ArrowLeft className="h-3.5 w-3.5" /> Memberships
        </button>
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-900">
            <CalendarDays className="h-4.5 w-4.5 text-brand-600" />
            Tune-Up Planner
            <span className="font-normal text-slate-400">— {segment.seg ? segment.seg.name : `${sn.label} · ${sn.checkLabel}`}</span>
            <InfoTip label="How the Tune-Up Planner works">Pick a light tech-day and Pegasus fills it with the nearest still-owed checks around what's already booked, in drive order, with the windows set. Then call down the list and tap Booked for each one.</InfoTip>
          </h2>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-hairline bg-white text-xs font-medium">
            <button type="button" onClick={() => setMode("week")} className={cn("px-2.5 py-1.5", mode === "week" ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50")}><CalendarDays className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />Week</button>
            <button type="button" onClick={() => setMode("map")} className={cn("px-2.5 py-1.5", mode === "map" ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50")} title="The whole season on the map — draw a loop or pick a city, then put the batch on a tech-day"><Lasso className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />Map</button>
            <button type="button" onClick={() => setMode("proposals")} className={cn("px-2.5 py-1.5", mode === "proposals" ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50")} title="What Pegasus proposed for the next ten working days — approve, adjust or skip a day at a time">
              <Inbox className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />Proposals{proposedCount > 0 && <span className={cn("ml-1.5 rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums", mode === "proposals" ? "bg-white/20 text-white" : "bg-brand-700 text-white")}>{proposedCount}</span>}
            </button>
          </div>
          <button type="button" onClick={() => setRulesOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
            <Settings2 className="h-3.5 w-3.5" /> Rules <span className="text-slate-400">· {rules.cap} a day · keep {rules.reserve} for no-heat</span>
          </button>
          <button type="button" onClick={() => navigate("/agreements")} className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
            Season Board
          </button>
        </div>
      </div>

      {/* Season strip */}
      {!segId && (
        <div className="mb-3 grid grid-cols-5 gap-2">
          <Stat label="Still to do" value={remaining.toLocaleString()} sub={`of ${totals.owed.toLocaleString()} owed this ${sn.kind}`} />
          <Stat label="Booked" value={totals.booked.toLocaleString()} sub="on the Board, not done" />
          <Stat label="Done" value={totals.done.toLocaleString()} sub={lastSeason ? `last ${sn.kind} by today: ${lastSeason.last_done_by_point.toLocaleString()} of ${lastSeason.last_total.toLocaleString()}` : `since ${sn.kind === "fall" ? "Sep 1" : "May 1"}`} tone="good" />
          <Stat label="Pace needed" value={`${pc.perWeek}`} unit="/ wk" sub={`about ${pc.techDaysPerDay} tech-days a day`} />
          <Stat label="Finish line" value={fmtDay(finishISO, { month: "short", day: "numeric" })} sub={`${weeksLeft} week${weeksLeft === 1 ? "" : "s"} left`} />
        </div>
      )}

      {/* Body */}
      {mode === "map" ? (
        <MapMode pool={unplaced} poolLoading={poolLoading} rules={rules} days={daysQ.data ?? []} seasonLabel={segment.seg ? segment.seg.name : sn.label} checkLabel={segment.seg ? "visits" : sn.checkLabel.toLowerCase()} onPlaced={placeOn} />
      ) : mode === "proposals" ? (
        <ProposalsMode days={daysQ.data ?? []} rules={rules} perWeek={pc.perWeek} finishISO={finishISO} seasonRows={seasonRows ?? []} onOpen={placeOn} onRules={() => setRulesOpen(true)} />
      ) : (
      <div className="grid min-h-0 flex-1 grid-cols-[600px_minmax(0,1fr)] items-start gap-4">
        {/* Week grid */}
        <div>
          <div className="mb-2 flex items-center gap-2 px-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Week of {fmtDay(days[0], { month: "short", day: "numeric" })}</span>
            <span className="ml-auto inline-flex items-center gap-1">
              <button type="button" onClick={() => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() - 7); return d; })} className="rounded-md border border-hairline bg-white p-1 text-slate-500 hover:bg-slate-50" aria-label="Previous week"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => setMonday(weekMonday())} className="rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50">This week</button>
              <button type="button" onClick={() => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() + 7); return d; })} className="rounded-md border border-hairline bg-white p-1 text-slate-500 hover:bg-slate-50" aria-label="Next week"><ChevronRight className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => void refetchCells()} title="Re-read the Board" className="ml-1 rounded-md border border-hairline bg-white p-1 text-slate-500 hover:bg-slate-50" aria-label="Refresh"><RefreshCw className={cn("h-3.5 w-3.5", cellsLoading && "animate-spin")} /></button>
            </span>
          </div>

          {cellsLoading && !cells ? (
            <Card className="p-6 text-center text-xs text-slate-500">Reading the Board…</Card>
          ) : (
            <div className="grid gap-1.5" style={{ gridTemplateColumns: "96px repeat(5, minmax(0, 1fr))" }}>
              <div />
              {days.map((d) => (
                <div key={d} className="pb-1 text-center">
                  <div className={cn("text-xs font-bold", d === isoDate(new Date()) ? "text-brand-700" : "text-slate-800")}>{fmtDay(d, { weekday: "short" })} <span className="tabular-nums">{fmtDay(d, { day: "numeric" })}</span></div>
                  {(pickedByDay.get(d) ?? 0) > 0 ? (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700"><Hand className="h-2.5 w-2.5" /> {pickedByDay.get(d)} picked this day</div>
                  ) : <div className="h-[18px]" />}
                </div>
              ))}
              {techs.map((t) => (
                <TechRow key={t.empno} tech={t} days={days} today={today} cellFor={cellFor} rules={rules} sel={sel} onSelect={(day) => setSel(sel?.empno === t.empno && sel.day === day ? null : { empno: t.empno, day })} />
              ))}
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[11px] text-slate-500">
            <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-slate-700 align-middle" /> on the Board (ESC + Pegasus)</span>
            <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-brand-300 align-middle" /> proposed, not booked yet</span>
            <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-slate-200 align-middle" /> room</span>
            <span className="inline-flex items-center gap-1"><Hand className="h-3 w-3 text-amber-600" /> members who picked that date online</span>
          </div>

          <Card className="mt-3 px-3 py-2.5 text-xs leading-relaxed text-slate-600">
            <b className="text-slate-800">This week's room:</b> {openTechDays} tech-days open, room for {roomThisWeek} checks{proposedThisWeek > 0 ? ` (${proposedThisWeek} proposed so far)` : ""}.
            {!segId && <> You need {pc.perWeek} a week to hit {fmtDay(finishISO, { month: "short", day: "numeric" })} — about {pc.techDaysPerDay} filled tech-days a day.</>}
            {poolLoading ? " Loading the members…" : ` ${unplaced.length.toLocaleString()} ${segId ? "members" : "still-owed visits"} with a map pin are not on a day yet.`}
          </Card>
        </div>

        {/* Build-a-day panel */}
        <div className="min-w-0">
          {sel && selCell && selTech ? (
            <BuildDayPanel
              key={`${sel.empno}|${sel.day}`}
              cell={selCell}
              tech={selTech}
              plan={selPlan}
              rules={rules}
              pool={unplaced}
              seasonLabel={segment.seg ? segment.seg.name : sn.label}
              checkLabel={segment.seg ? "visits" : sn.checkLabel.toLowerCase()}
              onClose={() => setSel(null)}
            />
          ) : (
            <Card className="flex min-h-[420px] flex-col items-center justify-center p-8 text-center">
              <CalendarDays className="h-9 w-9 text-slate-300" />
              <p className="mt-3 max-w-md text-sm font-semibold text-slate-700">Pick a tech-day on the left.</p>
            </Card>
          )}
        </div>
      </div>

      )}

      <RulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} rules={rules} homes={homes} />
      <CallViewer call={viewCall} onClose={() => setViewCall(null)} />
    </div>
    </PlannerViewCtx.Provider>
  );
}

function Stat({ label, value, unit, sub, tone }: { label: string; value: string; unit?: string; sub: string; tone?: "good" }) {
  return (
    <div className="rounded-xl border border-hairline bg-white px-3.5 py-2.5 shadow-e1">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className={cn("font-display text-xl font-bold tabular-nums tracking-tight", tone === "good" ? "text-emerald-700" : "text-slate-900")}>{value}{unit ? <span className="ml-1 text-xs font-semibold text-slate-400">{unit}</span> : null}</div>
      <div className="text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}

// ----------------------------------------------------------------------------
function TechRow({ tech, days, today, cellFor, rules, sel, onSelect }: {
  tech: { empno: string; name: string };
  days: string[];
  today: string;
  cellFor: (empno: string, day: string) => TechDayCell | null;
  rules: PlannerRules;
  sel: { empno: string; day: string } | null;
  onSelect: (day: string) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 pr-1 text-xs font-semibold text-slate-800">
        <TechAvatar empno={tech.empno} name={tech.name} size="xs" />
        <span className="truncate">{firstName(tech.name)}</span>
      </div>
      {days.map((day) => {
        const c = cellFor(tech.empno, day);
        const selected = sel?.empno === tech.empno && sel.day === day;
        if (!c) return <div key={day} className="min-h-[72px] rounded-lg border border-dashed border-hairline" />;
        if (day < today) {
          return (
            <div key={day} className="min-h-[72px] rounded-lg border border-hairline bg-slate-50/60 px-2 py-1.5 text-slate-400" title="Already happened">
              <div className="text-[11.5px] font-semibold leading-tight">{c.off ? "Off" : c.calls > 0 ? `${c.calls} call${c.calls === 1 ? "" : "s"}${c.cities ? ` · ${c.cities}` : ""}` : "Nothing"}</div>
              <div className="text-[11px]">done</div>
            </div>
          );
        }
        const room = roomFor(c, rules);
        const proposed = Math.max(0, (c.plan_stops ?? 0) - (c.plan_booked ?? 0));
        const total = Math.max(rules.cap, c.calls + proposed);
        return (
          <button
            key={day}
            type="button"
            onClick={() => onSelect(day)}
            className={cn(
              "min-h-[72px] rounded-lg border px-2 py-1.5 text-left transition-colors",
              c.off ? "border-dashed border-hairline bg-slate-50 text-slate-400" : "border-hairline bg-white hover:bg-slate-50",
              selected && "border-brand-500 ring-2 ring-brand-500/25",
            )}
          >
            {c.off ? (
              <div className="text-[11.5px] font-semibold capitalize">{c.off_label === "off" ? "Off" : c.off_label ?? "Off"}</div>
            ) : (
              <>
                <div className="text-[11.5px] font-semibold leading-tight text-slate-800">
                  {c.calls > 0 ? `${c.calls} call${c.calls === 1 ? "" : "s"}${c.cities ? ` · ${c.cities}` : ""}` : "Open"}
                  {c.off_label === "half day" && <span className="font-medium text-slate-400"> · half day</span>}
                  {c.pickup_when && <span title={c.pickup_when === "start" ? "Starts the day with a part pickup" : "Part pickup mid-day"} className="ml-1 inline-flex align-middle text-amber-600"><Package className="h-3 w-3" /></span>}
                </div>
                <div className="text-[11px] tabular-nums text-slate-500">
                  {proposed > 0 ? `+${proposed} proposed · room for ${Math.max(0, room - proposed)}` : `room for ${room}`}
                  {(c.plan_booked ?? 0) > 0 && c.plan_origin ? "" : null}
                </div>
                <div className="mt-1.5 flex gap-px">
                  {Array.from({ length: total }).map((_, i) => (
                    <i key={i} className={cn("block h-[5px] flex-1 rounded-[2px]", i < c.calls ? "bg-slate-700" : i < c.calls + proposed ? "bg-brand-300" : i < rules.cap - rules.reserve ? "bg-slate-200" : "bg-slate-100")} />
                  ))}
                </div>
              </>
            )}
          </button>
        );
      })}
    </>
  );
}

// ============================================================================
// Build a day
// ============================================================================
function BuildDayPanel({ cell, tech, plan, rules, pool, seasonLabel, checkLabel, onClose }: {
  cell: TechDayCell;
  tech: { empno: string; name: string };
  plan: VisitDay | null;
  rules: PlannerRules;
  pool: PoolMember[];
  seasonLabel: string;
  checkLabel: string;
  onClose: () => void;
}) {
  const { data: callsData, isLoading: callsLoading } = useTechDayCalls(cell.empno, cell.day);
  const calls = useMemo(() => callsData ?? [], [callsData]);
  const createDay = useCreateDay();
  const updateDay = useUpdateDay();
  const deleteDay = useDeleteDay();
  const addStops = useAddStops();
  const updateStop = useUpdateStop();
  const removeStop = useRemoveStop();
  const reorder = useReorderStops();
  const bookStop = useBookStop();
  const unbook = useUnbookStop();

  const first = firstName(tech.name);
  const view = usePlannerView();
  const anchorInfo = useMemo(() => dayAnchor(cell, rules), [cell, rules]);
  const anchor: LatLng | null = useMemo(() => (plan && typeof plan.anchor_lat === "number" && typeof plan.anchor_lng === "number" ? { lat: plan.anchor_lat, lng: plan.anchor_lng } : anchorInfo.at), [plan, anchorInfo]);

  const stops = useMemo(() => plan?.stops ?? [], [plan]);
  const { rows, unmapped } = useMemo(() => buildRows(calls, stops, anchor, rules.office), [calls, stops, anchor, rules.office]);
  const numbered = useMemo(() => { let n = 0; return rows.map((r) => (r.pickup ? null : ++n)); }, [rows]);
  const startPickup = rows.find((r) => r.pickup?.when === "start")?.pickup ?? null;
  const midPickups = rows.filter((r) => r.pickup?.when === "mid").length;
  const room = roomFor(cell, rules);
  const openStops = stops.filter((s) => s.status !== "booked" && s.status !== "skipped");
  const bookedStops = stops.filter((s) => s.status === "booked");
  const pickedOpen = openStops.filter((s) => s.priority === "picked");
  const miles = useMemo(() => routeMiles(rows.map(rowPos)), [rows]);

  // Persist the route's windows whenever the sequence changes (free stops only).
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    if (!plan || reorder.isPending) return;
    const orderedIds: string[] = [];
    const windows: Record<string, { arrival_window: string; eta_hour: number }> = {};
    let changed = false;
    rows.forEach((r) => {
      if (!r.stop) return;
      orderedIds.push(r.stop.id);
      const eta = Math.round(r.seq.eta * 4) / 4;
      if (r.stop.status !== "booked" && !r.stop.window_locked) {
        windows[r.stop.id] = { arrival_window: r.seq.window, eta_hour: eta };
        if (r.stop.arrival_window !== r.seq.window || r.stop.eta_hour == null || Math.abs(Number(r.stop.eta_hour) - eta) > 0.01) changed = true;
      }
    });
    for (const u of unmapped) orderedIds.push(u.id);
    const positionsChanged = orderedIds.some((id, i) => (stops.find((s) => s.id === id)?.position ?? -1) !== i);
    if (changed || positionsChanged) reorder.mutate({ orderedIds, windows });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map((r) => `${r.seq.id}:${r.seq.window}`).join("|")]);

  // ---- Fill this day ----
  const fill = async () => {
    if (busy) return;
    setBusy("fill");
    try {
      const date = cell.day;
      const cands = pickCandidates(anchor, pool.map((p) => ({ ...p, pickedForDate: p.pickedDate === date })), room, rules.maxMiles);
      if (cands.length === 0) { toastError(`Nothing still owed within ${rules.maxMiles} miles${anchor ? ` of ${anchorInfo.kind === "home" ? first + "'s home" : anchorInfo.kind === "pickup" ? "the pickup" : "his calls"}` : " — no home address for " + first + " in ESC; add from the map"}.`); return; }
      const nPicked = cands.filter((c) => c.pickedDate === date).length;
      const nRenew = cands.filter((c) => c.renewal).length;
      const nRisk = cands.filter((c) => c.atRisk).length;
      const where = cell.calls > 0 && cell.cities ? `where ${first} already is ${fmtDay(date, { weekday: "long" })} (${cell.cities})`
        : anchorInfo.kind === "pickup" ? `${first}'s first stop — ${anchorInfo.label} (part pickup)`
        : anchorInfo.kind === "home" ? `${first}'s home (${anchorInfo.label})`
        : nPicked > 0 ? "the members who asked for this day" : "each other";
      const reason = [`Nearest still-owed ${checkLabel} to ${where}`, nPicked > 0 ? `${nPicked} asked for this day` : null, nRenew > 0 ? `${nRenew} renewal${nRenew === 1 ? "" : "s"} at this visit` : null, nRisk > 0 ? `${nRisk} at risk` : null].filter(Boolean).join(" · ") + ".";
      let dayId = plan?.id ?? null;
      if (!dayId) {
        dayId = await createDay.mutateAsync({ title: `${first} · ${fmtDay(date)}`, plan_date: date, tech_empno: cell.empno, source_label: seasonLabel, origin: "fill", anchor_lat: anchor?.lat ?? null, anchor_lng: anchor?.lng ?? null, reason });
      } else {
        await updateDay.mutateAsync({ id: dayId, reason, anchor_lat: anchor?.lat ?? null, anchor_lng: anchor?.lng ?? null });
      }
      await addStops.mutateAsync({ dayId, stops: stopsForDay(calls, cands, anchor, date, rules.office), startPosition: plan?.stops.length ?? 0 });
    } catch (e) {
      toastError(`Couldn't fill the day — ${(e as Error).message}`);
    } finally { setBusy(null); }
  };

  // ---- Add members (map pick / loop / swap) ----
  const addMembers = async (list: PoolMember[], origin: "manual" | "loop" = "manual", area: LatLng[] | null = null) => {
    if (list.length === 0) return;
    let dayId = plan?.id ?? null;
    if (!dayId) dayId = await createDay.mutateAsync({ title: `${first} · ${fmtDay(cell.day)}`, plan_date: cell.day, tech_empno: cell.empno, source_label: seasonLabel, origin, area: area ? area.map((q) => [q.lat, q.lng] as [number, number]) : null, anchor_lat: anchor?.lat ?? null, anchor_lng: anchor?.lng ?? null });
    await addStops.mutateAsync({ dayId, stops: stopsForDay(calls, list, anchor, cell.day, rules.office), startPosition: plan?.stops.length ?? 0 });
  };
  const addMember = (m: PoolMember) => addMembers([m]);
  const [drawing, setDrawing] = useState(false);
  const onLoop = (path: LatLng[]) => {
    setDrawing(false);
    const inDay = new Set(stops.map((s) => `${s.custno}|${s.locno}`));
    const inside = pool.filter((p) => !inDay.has(`${p.custno}|${p.locno}`) && insideLoop(p, path.map((q) => [q.lat, q.lng] as [number, number])));
    if (inside.length === 0) { toastError("Nobody still owed inside that loop."); return; }
    const left = Math.max(0, room - openStops.length);
    const from = anchor ?? centroid(inside);
    const take = [...inside].sort((a, b) => haversineMiles(from, a) - haversineMiles(from, b)).slice(0, Math.max(1, left));
    if (inside.length > take.length) toastError(`${inside.length} inside the loop — added the nearest ${take.length}, the day is full after that.`);
    void addMembers(take, "loop", path).catch((e) => toastError(`Couldn't add — ${(e as Error).message}`));
  };
  const swap = async (s: VisitDayStop) => {
    if (busy) return;
    setBusy(s.id);
    try {
      const here = usableCoords(s.lat, s.lng) ? { lat: s.lat as number, lng: s.lng as number } : anchor;
      const next = pickCandidates(here, pool.filter((p) => p.key !== s.visit_id && `${p.custno}|${p.locno}` !== `${s.custno}|${s.locno}`).map((p) => ({ ...p, pickedForDate: p.pickedDate === cell.day })), 1, rules.maxMiles)[0];
      if (!next) { toastError("Nobody else still owed nearby."); return; }
      await removeStop.mutateAsync(s.id);
      await addMember(next);
    } finally { setBusy(null); }
  };

  const decide = useDecideProposal();
  const approveIfProposed = async () => {
    if (plan && plan.status === "proposed") await decide.mutateAsync({ dayId: plan.id, action: "approve" });
  };
  const bookAllPicked = async () => {
    if (busy || !plan) return;
    setBusy("bookall");
    try {
      await approveIfProposed();
      for (const s of pickedOpen) {
        const win = s.window_locked ? s.arrival_window : (rows.find((r) => r.stop?.id === s.id)?.seq.window ?? s.arrival_window);
        await bookStop.mutateAsync({ stop: s, dayTitle: plan.title, date: cell.day, window: win ?? null, tech: cell.empno });
      }
    } catch (e) { toastError(`Couldn't book — ${(e as Error).message}`); }
    finally { setBusy(null); }
  };

  // ---- Map ----
  const [addMode, setAddMode] = useState(false);
  const mapPoints = useMemo((): MapPoint[] => {
    const pts: MapPoint[] = [];
    rows.forEach((r, i) => {
      const { lat, lng } = rowPos(r);
      if (r.pickup) {
        pts.push({ id: r.seq.id, lat, lng, label: r.pickup.label, sublabel: r.pickup.when === "start" ? "first thing" : r.seq.window, shape: "waypoint", colorHex: "#d97706", colorKey: "day",
          hover: { title: r.pickup.label, chips: [{ text: r.pickup.when === "start" ? "starts the day here" : "mid-day stop" }, { text: `~${fmtHour(r.seq.eta)} · 15 min` }], note: r.call?.notes ?? undefined } });
        return;
      }
      const name = r.call ? displayCustomerName(null, r.call.name) || r.call.custno : stopName(r.stop!);
      const booked = r.stop?.status === "booked";
      pts.push({
        id: r.seq.id, lat, lng, label: name, sublabel: r.seq.window, order: numbered[i] ?? i + 1,
        colorHex: r.call ? "#334155" : booked ? "#059669" : "#1d4ed8", colorKey: "day",
        hover: { title: name, chips: [{ text: r.seq.window }, { text: r.call ? "on the Board" : booked ? "Booked" : STOP_STATUS_LABEL[r.stop!.status] }], meta: [r.call?.address ?? r.stop?.address, r.call?.city ?? r.stop?.city].filter(Boolean).join(" · ") || undefined, note: r.call?.notes ?? r.stop?.context ?? undefined },
      });
    });
    if (addMode) {
      for (const p of pool) {
        if (stops.some((s) => s.visit_id === p.visit_id && p.visit_id) || stops.some((s) => `${s.custno}|${s.locno}` === `${p.custno}|${p.locno}`)) continue;
        pts.push({ id: `pool:${p.key}`, lat: p.lat, lng: p.lng, label: p.name, sublabel: p.city ?? undefined, colorHex: p.pickedDate === cell.day ? "#d97706" : p.atRisk ? "#e11d48" : p.renewal ? "#7c3aed" : "#94a3b8", colorKey: "pool",
          hover: { title: p.name, chips: [{ text: p.pickedDate === cell.day ? "asked for this day" : p.atRisk ? "at risk" : p.renewal ? "renewal at this visit" : "still owed" }], meta: [p.address, p.city].filter(Boolean).join(" · ") || undefined, note: p.context } });
      }
    }
    return pts;
  }, [rows, numbered, addMode, pool, stops, cell.day]);
  const poolByKey = useMemo(() => new Map(pool.map((p) => [p.key, p])), [pool]);
  const onMapSelect = (id: string) => {
    if (id.startsWith("pool:")) {
      const p = poolByKey.get(id.slice(5));
      if (p) void addMember(p).catch((e) => toastError(`Couldn't add — ${(e as Error).message}`));
      return;
    }
    if (id.startsWith("call:")) {
      const c = calls.find((x) => `call:${x.id}` === id);
      if (c) view.openCall({ source: c.source, id: c.id });
      return;
    }
    if (id.startsWith("stop:")) {
      const st = stops.find((x) => `stop:${x.id}` === id);
      if (st) view.openCustomer(st.custno);
    }
  };

  // ---- Export / print ----
  const exportCsv = () => {
    const out: (string | number | null)[][] = [["stop", "eta", "window", "customer", "address", "city", "zip", "phone", "custno", "context", "status"]];
    rows.forEach((r, i) => out.push([numbered[i], fmtHour(r.seq.eta), r.seq.window, r.pickup ? r.pickup.label : r.call ? displayCustomerName(null, r.call.name) : stopName(r.stop!), r.call?.address ?? r.stop!.address, r.call?.city ?? r.stop!.city, r.stop?.zip ?? null, r.stop?.phone ?? null, r.call?.custno ?? r.stop!.custno, r.call?.notes ?? r.stop!.context, r.pickup ? "part pickup" : r.call ? "on the Board" : STOP_STATUS_LABEL[r.stop!.status]]));
    downloadCsv(`${first.toLowerCase()}-${cell.day}.csv`, out);
  };
  const printSheet = () => {
    const esc = (s: string | null | undefined) => (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const body = rows.map((r, i) => `<tr><td>${numbered[i] ?? "—"}</td><td>${esc(r.seq.window)}<br/><span class="sub">~${esc(fmtHour(r.seq.eta))}</span></td><td><strong>${esc(r.pickup ? r.pickup.label : r.call ? displayCustomerName(null, r.call.name) : stopName(r.stop!))}</strong><br/><span class="sub">${esc(r.call?.notes ?? r.stop!.context)}</span></td><td>${esc(r.call?.address ?? r.stop!.address)}<br/>${esc(r.call?.city ?? r.stop!.city)} ${esc(r.stop?.zip ?? "")}</td><td>${esc(r.stop?.phone ?? "")}</td><td>${r.pickup ? "part pickup" : r.call ? "on the Board" : esc(STOP_STATUS_LABEL[r.stop!.status])}</td><td class="notes"></td></tr>`).join("");
    const html = `<!doctype html><html><head><title>${esc(first)} — ${esc(fmtDay(cell.day, { weekday: "long", month: "long", day: "numeric" }))} call sheet</title><style>
      body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#0f172a} h1{font-size:20px;margin:0 0 2px} .meta{color:#475569;font-size:13px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse;font-size:12px} th{background:#f1f5f9;text-align:left;padding:6px 8px;border:1px solid #cbd5e1;text-transform:uppercase;font-size:10px;letter-spacing:.04em;color:#475569}
      td{padding:6px 8px;border:1px solid #cbd5e1;vertical-align:top} .sub{color:#64748b;font-size:11px} .notes{width:150px}</style></head><body>
      <h1>${esc(tech.name)} — ${esc(fmtDay(cell.day, { weekday: "long", month: "long", day: "numeric" }))}</h1>
      <div class="meta">${rows.length} stops · ~${miles.toFixed(0)} mi · Sedgwick Heating — Tune-Up Planner</div>
      <table><thead><tr><th>#</th><th>Window</th><th>Customer</th><th>Address</th><th>Phone</th><th>Status</th><th>Notes</th></tr></thead><tbody>${body}</tbody></table>
      <script>window.print()</script></body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const existingCalls = calls.filter((c) => !c.pseudo && !(c.source === "pegasus" && stops.some((s) => s.dispatch_id === c.id)));

  return (
    <Card className="overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center gap-3 border-b border-hairline px-4 py-3">
        <TechAvatar empno={cell.empno} name={tech.name} size="md" />
        <div className="min-w-0">
          <div className="font-display text-base font-bold tracking-tight text-slate-900">{first} · {fmtDay(cell.day, { weekday: "long", month: "short", day: "numeric" })}</div>
          <div className="text-xs text-slate-500">
            {cell.off ? `Off${cell.off_label && cell.off_label !== "off" ? ` (${cell.off_label})` : ""} — nothing to fill.` : (
              <>
                {existingCalls.length > 0 ? `${existingCalls.length} call${existingCalls.length === 1 ? "" : "s"} already on the Board${cell.cities ? ` (${cell.cities})` : ""}` : "Nothing on the Board yet"}
                {startPickup ? ` · starts with a part pickup at ${startPickup.label.replace(/^Part pickup · /, "")}` : midPickups > 0 ? ` · part pickup mid-day` : existingCalls.length === 0 && anchorInfo.kind === "home" ? ` · starts from home (${anchorInfo.label})` : ""}
                {stops.length > 0 ? ` · ${openStops.length} proposed · ${bookedStops.length} booked` : ""}
                {rows.length > 1 ? ` · ${rows.length} stops · ~${miles.toFixed(0)} mi · ~${Math.round(driveMinutes(miles))} min driving` : ""}
                {` · room for ${Math.max(0, room - openStops.length)}`}
              </>
            )}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
          {!cell.off && (
            <button type="button" onClick={() => { setAddMode((v) => !v); setDrawing(false); }} className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors", addMode ? "border-brand-300 bg-brand-50 text-brand-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")} title="Show every still-owed member on the map — click one to add it to this day">
              <MapPin className="h-3.5 w-3.5" /> {addMode ? "Done adding" : "Add from map"}
            </button>
          )}
          {!cell.off && addMode && (
            <button type="button" onClick={() => setDrawing((v) => !v)} className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors", drawing ? "border-brand-700 bg-brand-700 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")} title="Drag a loop on the map — everyone still owed inside joins this day (nearest first, up to the room left)">
              <Lasso className="h-3.5 w-3.5" /> {drawing ? "Drawing…" : "Draw a loop"}
            </button>
          )}
          {rows.length > 0 && (
            <>
              <button type="button" onClick={printSheet} className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"><Printer className="h-3.5 w-3.5" /> Call sheet</button>
              <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> CSV</button>
            </>
          )}
          {pickedOpen.length > 0 && plan && (
            <button type="button" onClick={() => void bookAllPicked()} disabled={busy != null} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300">
              <Check className="h-3.5 w-3.5" /> Book the {pickedOpen.length} who asked for this day
            </button>
          )}
          {!cell.off && !plan && (
            <button type="button" onClick={() => void fill()} disabled={busy != null || callsLoading || room <= 0} title={room <= 0 ? "No room left under the cap" : `Pegasus proposes up to ${room} nearby ${checkLabel}`} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300">
              <Sparkles className="h-3.5 w-3.5" /> {busy === "fill" ? "Filling…" : `Fill this day (${room})`}
            </button>
          )}
          {plan && room - openStops.length > 0 && !cell.off && (
            <button type="button" onClick={() => void fill()} disabled={busy != null} className="inline-flex items-center gap-1.5 rounded-md border border-brand-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50">
              <Sparkles className="h-3.5 w-3.5" /> {busy === "fill" ? "Filling…" : `Fill the rest (${room - openStops.length})`}
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {plan?.status === "proposed" && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50/70 px-4 py-2 text-xs text-amber-900">
          <Moon className="h-3.5 w-3.5 text-amber-600" /> <b>Pegasus proposed this day.</b> Booking any stop keeps it; or decide now:
          <button type="button" onClick={() => decide.mutate({ dayId: plan.id, action: "approve" })} disabled={decide.isPending} className="ml-1 inline-flex items-center gap-1 rounded-md bg-brand-700 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:opacity-50"><Check className="h-3 w-3" /> Keep it</button>
          <button type="button" onClick={() => { decide.mutate({ dayId: plan.id, action: "skip" }); onClose(); }} disabled={decide.isPending} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"><X className="h-3 w-3" /> Skip this one</button>
        </div>
      )}

      {/* Rows */}
      {callsLoading ? (
        <div className="p-6 text-center text-xs text-slate-500">Reading {first}'s Board…</div>
      ) : rows.length === 0 && unmapped.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs leading-relaxed text-slate-500">
          {cell.off ? "This tech is off." : <>Nothing on this day yet. <b className="text-slate-700">Fill this day</b> proposes the nearest still-owed {checkLabel}{anchorInfo.kind === "home" ? ` to ${first}'s home (${anchorInfo.label})` : anchorInfo.kind === "pickup" ? ` to ${anchorInfo.label}, where ${first} picks up a part first` : anchor ? "" : ` — ${first} has no home address in ESC and nothing on the Board to start from, so click members on the map`}.</>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[28px_minmax(0,1fr)_172px_auto] gap-2.5 px-4 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
            <span>#</span><span>Stop · in drive order</span>
            <span className="inline-flex items-center gap-1.5">
              Window
              <InfoTip label="About arrival windows">The route sets each window from the drive order. Tap one to change it; a window you set stays put when the route re-orders.</InfoTip>
            </span>
            <span className="text-right">When you call</span>
          </div>
          <ul className="divide-y divide-slate-50">
            {rows.map((r, i) => (
              <li key={r.seq.id} className={cn("grid grid-cols-[28px_minmax(0,1fr)_172px_auto] items-center gap-2.5 px-4 py-2", r.pickup ? "bg-amber-50/50" : r.call && "bg-slate-50/70")}>
                {r.pickup ? <span className="flex h-5.5 w-5.5 items-center justify-center rounded-full bg-amber-500 text-white"><Package className="h-3 w-3" /></span>
                  : <span className={cn("flex h-5.5 w-5.5 items-center justify-center rounded-full text-[11px] font-bold text-white", r.call ? "bg-slate-700" : r.stop?.status === "booked" ? "bg-emerald-600" : "bg-brand-700")}>{numbered[i]}</span>}
                {r.pickup ? (
                  <>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-800">
                        <button type="button" onClick={() => view.openCall({ source: r.call!.source, id: r.call!.id })} title={`Open dispatch ${r.call!.id}`} className="truncate text-left hover:text-brand-700 hover:underline">{r.pickup.label}</button>
                        <span className="rounded bg-amber-100 px-1.5 py-px text-[10px] font-semibold text-amber-800">{r.pickup.when === "start" ? "starts the day here" : "mid-day stop"}</span>
                      </div>
                      {r.call!.notes && <div className="truncate text-[11px] text-slate-500">{r.call!.notes.slice(0, 90)}</div>}
                    </div>
                    <div className="flex items-center gap-1.5"><span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-amber-800">{r.pickup.when === "start" ? "first thing" : effectiveWindow(r.call!.window_code, r.call!.notes) || "any"}</span><span className="whitespace-nowrap text-[10px] text-slate-400">~{fmtHour(r.seq.eta)} · 15 min</span></div>
                    <div className="flex items-center justify-end gap-1 text-[11px] text-slate-400">
                      <span>not a customer call</span>
                      <button type="button" onClick={() => view.openCall({ source: r.call!.source, id: r.call!.id })} title="Open the ticket" className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-brand-700"><ExternalLink className="h-3 w-3" /></button>
                    </div>
                  </>
                ) : r.call ? (
                  <>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-800">
                        <button type="button" onClick={() => view.openCall({ source: r.call!.source, id: r.call!.id })} title={r.call.source === "esc" ? `Open dispatch ${r.call.id}` : "Open the Pegasus job"} className="truncate text-left hover:text-brand-700 hover:underline">{displayCustomerName(null, r.call.name) || r.call.custno}</button>
                        <span className="rounded bg-slate-200 px-1.5 py-px text-[10px] font-semibold text-slate-600">on the Board{r.call.source === "pegasus" ? " · Pegasus" : ""}</span>
                        {r.seq.late && <span className="rounded bg-rose-50 px-1.5 py-px text-[10px] font-semibold text-rose-700">tight — promised {effectiveWindow(r.call.window_code, r.call.notes)}</span>}
                      </div>
                      <div className="truncate text-[11px] text-slate-500">{[r.call.address, r.call.city].filter(Boolean).join(" · ")}{r.call.notes ? ` — ${r.call.notes.slice(0, 60)}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-1.5"><span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700">{effectiveWindow(r.call.window_code, r.call.notes) || "any"}{r.call.window_code && !/^(8AM|7-8|8-12|10-2|11-1|12-4|3-7)$/i.test(r.call.window_code) ? <span className="ml-1 font-medium text-slate-500">{r.call.window_code.replace(/-+$/, "")}</span> : null}</span><span className="whitespace-nowrap text-[10px] text-slate-400">fixed · ~{fmtHour(r.seq.eta)}</span></div>
                    <div className="flex items-center justify-end gap-1 text-[11px] text-slate-400">
                      <span>already booked · stays put</span>
                      <button type="button" onClick={() => view.openCall({ source: r.call!.source, id: r.call!.id })} title="Open the ticket" className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-brand-700"><ExternalLink className="h-3 w-3" /></button>
                    </div>
                  </>
                ) : (
                  <StopCells stop={r.stop!} seq={r.seq} first={first} busy={busy} plan={plan!} cell={cell}
                    onBook={(win) => void approveIfProposed().then(() => bookStop.mutate({ stop: r.stop!, dayTitle: plan!.title, date: cell.day, window: win, tech: cell.empno }, { onError: (e) => toastError(`Couldn't book — ${(e as Error).message}`) }))}
                    onStatus={(status) => updateStop.mutate({ id: r.stop!.id, status })}
                    onWindow={(win) => updateStop.mutate({ id: r.stop!.id, arrival_window: win, window_locked: true })}
                    onUnlockWindow={() => updateStop.mutate({ id: r.stop!.id, window_locked: false })}
                    onSwap={() => void swap(r.stop!)}
                    onUnbook={() => unbook.mutate(r.stop!)}
                    onRemove={() => removeStop.mutate(r.stop!.id)}
                    pending={bookStop.isPending || updateStop.isPending || removeStop.isPending || unbook.isPending}
                  />
                )}
              </li>
            ))}
            {unmapped.map((s) => (
              <li key={s.id} className="grid grid-cols-[28px_minmax(0,1fr)_172px_auto] items-center gap-2.5 px-4 py-2">
                <span className="flex h-5.5 w-5.5 items-center justify-center rounded-full bg-amber-500 text-white"><MapPinOff className="h-3 w-3" /></span>
                <div className="min-w-0"><div className="truncate text-xs font-semibold text-slate-800">{stopName(s)}</div><div className="text-[11px] text-amber-700">No map pin — on the list, not the route.</div></div>
                <div />
                <div className="text-right"><button type="button" onClick={() => removeStop.mutate(s.id)} className="rounded p-0.5 text-slate-300 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button></div>
              </li>
            ))}
          </ul>
          {plan?.reason && (
            <div className="border-t border-hairline bg-slate-50/60 px-4 py-2 text-[11.5px] leading-relaxed text-slate-600">
              <Sparkles className="mr-1 inline h-3 w-3 text-brand-600 align-[-2px]" /><b className="text-slate-800">Why these:</b> {plan.reason}
            </div>
          )}
        </>
      )}

      {/* Map */}
      {!cell.off && (
        <div className="relative h-[300px] border-t border-hairline">
          <DispatchMap points={mapPoints} onSelectPoint={onMapSelect} colorForKey={(k) => (k === "pool" ? "#94a3b8" : "#1d4ed8")} showRoutes viewStorageKey="pegasus.planner.map.view" drawMode={addMode && drawing} onDrawEnd={onLoop} loops={plan?.area && plan.area.length >= 3 ? [{ id: plan.id, path: plan.area.map(([lat, lng]) => ({ lat, lng })), color: "#7c3aed" }] : undefined} />
          <div className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-white/90 px-2 py-1 text-[10.5px] text-slate-600 shadow-e1">
            <span className="text-slate-700">●</span> already on the Board&nbsp; <span className="text-brand-700">●</span> proposed, in drive order&nbsp; <span className="text-emerald-600">●</span> booked{(startPickup || midPickups > 0) && <>&nbsp; <span className="text-amber-600">◆</span> part pickup</>}
            {addMode && <>&nbsp; <span className="text-slate-400">●</span> still owed — click to add</>}
          </div>
        </div>
      )}

      {/* Footer */}
      {plan && (
        <div className="flex items-center gap-2 border-t border-hairline bg-slate-50/60 px-4 py-2 text-[11px] text-slate-500">
          <RouteIcon className="h-3 w-3" /> Saved as “{plan.title}” · {plan.origin === "fill" ? "filled by Pegasus" : plan.origin === "loop" ? "from a loop" : plan.origin === "proposal" ? "a nightly proposal" : "built by hand"}
          {plan.status !== "done" && openStops.length === 0 && stops.length > 0 && (
            <button type="button" onClick={() => updateDay.mutate({ id: plan.id, status: "done" })} className="ml-2 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-emerald-700"><Check className="h-3 w-3" /> Mark done</button>
          )}
          <span className="ml-auto" />
          {confirmDelete ? (
            <span className="inline-flex items-center gap-1">
              <button type="button" onClick={() => { deleteDay.mutate(plan.id); setConfirmDelete(false); }} className="rounded-md bg-rose-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-rose-700">Delete the plan (booked visits stay)</button>
              <button type="button" onClick={() => setConfirmDelete(false)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><X className="h-3 w-3" /></button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 hover:border-rose-200 hover:text-rose-600"><Trash2 className="h-3 w-3" /> Delete plan</button>
          )}
        </div>
      )}
    </Card>
  );
}

function StopCells({ stop, seq, busy, pending, onBook, onStatus, onWindow, onUnlockWindow, onSwap, onUnbook, onRemove }: {
  stop: VisitDayStop; seq: SequencedStop; first: string; busy: string | null; plan: VisitDay; cell: TechDayCell; pending: boolean;
  onBook: (win: string | null) => void; onStatus: (s: VisitDayStop["status"]) => void; onWindow: (w: string) => void; onUnlockWindow: () => void; onSwap: () => void; onUnbook: () => void; onRemove: () => void;
}) {
  const booked = stop.status === "booked";
  const win = booked ? (stop.booked_window ?? seq.window) : stop.window_locked ? (stop.arrival_window ?? seq.window) : seq.window;
  const view = usePlannerView();
  return (
    <>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-800">
          <button type="button" onClick={() => view.openCustomer(stop.custno)} title={`Open ${stopName(stop)} — #${stop.custno}`} className="truncate text-left hover:text-brand-700 hover:underline">{stopName(stop)}</button>
          {stop.priority === "picked" && <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700"><Hand className="h-2.5 w-2.5" /> {stop.context?.startsWith("asked") ? stop.context : "asked for this day"}</span>}
          {stop.priority === "renewal" && <span className="rounded bg-violet-50 px-1.5 py-px text-[10px] font-semibold text-violet-700">renewal at this visit</span>}
          {stop.priority === "at_risk" && <span className="rounded bg-rose-50 px-1.5 py-px text-[10px] font-semibold text-rose-700">at risk</span>}
          {stop.status === "no_answer" && <span className="rounded bg-slate-100 px-1.5 py-px text-[10px] font-semibold text-slate-500">No answer</span>}
          {stop.status === "requested" && <span className="rounded bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700">Wants a date</span>}
          {booked && <span className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-px text-[10px] font-semibold text-emerald-700"><Check className="h-2.5 w-2.5" /> Booked · confirmation queued</span>}
        </div>
        <div className="truncate text-[11px] text-slate-500">{[stop.address, stop.city].filter(Boolean).join(" · ") || stop.context}</div>
      </div>
      <div className="flex items-center gap-1.5">
        {booked ? (
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-emerald-700">{win}</span>
        ) : (
          <span className="relative inline-flex items-center">
            <select value={win} onChange={(e) => onWindow(e.target.value)} className={cn("appearance-none rounded-md border py-0.5 pl-1.5 pr-5 text-[11px] font-semibold tabular-nums", stop.window_locked ? "border-amber-300 bg-amber-50 text-amber-800" : "border-brand-200 bg-brand-50 text-brand-800")} aria-label="Arrival window">
              {WINDOWS.map((w) => <option key={w} value={w}>{w}</option>)}
              {!WINDOWS.includes(win as (typeof WINDOWS)[number]) && <option value={win}>{win}</option>}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1 h-3 w-3 text-slate-400" />
          </span>
        )}
        <span className="whitespace-nowrap text-[10px] text-slate-400">{booked ? "booked" : stop.window_locked ? <button type="button" onClick={onUnlockWindow} className="underline decoration-dotted" title="Let the route set it again">set by you</button> : `route · ~${fmtHour(seq.eta)}`}</span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1">
        {!booked && stop.phone && (
          <a href={`tel:${stop.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-50"><Phone className="h-2.5 w-2.5" /> Call</a>
        )}
        {!booked ? (
          <>
            <button type="button" onClick={() => onBook(win)} disabled={pending || busy != null} className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold text-white transition-colors disabled:opacity-50", stop.priority === "picked" ? "bg-brand-700 hover:bg-brand-800" : "bg-emerald-600 hover:bg-emerald-700")}>
              <Check className="h-2.5 w-2.5" /> {stop.priority === "picked" ? "Book — they asked for it" : "Booked"}
            </button>
            {stop.status !== "no_answer" && <button type="button" onClick={() => onStatus("no_answer")} disabled={pending} className="rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50">No answer</button>}
            {stop.status !== "requested" && <button type="button" onClick={() => onStatus("requested")} disabled={pending} title="They want a visit — waiting on a final date" className="rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50">Wants a date</button>}
            <button type="button" onClick={onSwap} disabled={pending || busy != null} title="Put the next-nearest still-owed member here instead" className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><RefreshCw className={cn("h-2.5 w-2.5", busy === stop.id && "animate-spin")} /> Swap</button>
          </>
        ) : (
          <>
            {stop.dispatch_id && <button type="button" onClick={() => view.openCall({ source: "pegasus", id: stop.dispatch_id! })} title="Open the booked visit" className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-brand-700 hover:bg-brand-50"><ExternalLink className="h-2.5 w-2.5" /> Open</button>}
            <button type="button" onClick={onUnbook} disabled={pending} title="Cancel the created visit and put this stop back to work" className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50"><Undo2 className="h-2.5 w-2.5" /> Un-book</button>
          </>
        )}
        <button type="button" onClick={onRemove} disabled={booked || pending} title={booked ? "Un-book first" : "Take off this day"} className="rounded p-0.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-rose-500 disabled:opacity-30"><X className="h-3 w-3" /></button>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
function RulesModal({ open, onClose, rules, homes }: { open: boolean; onClose: () => void; rules: PlannerRules; homes: { empno: string; name: string; label: string | null }[] }) {
  const set = useSetPlannerRule();
  const [cap, setCap] = useState(rules.cap);
  const [reserve, setReserve] = useState(rules.reserve);
  const [miles, setMiles] = useState(rules.maxMiles);
  useEffect(() => { setCap(rules.cap); setReserve(rules.reserve); setMiles(rules.maxMiles); }, [rules, open]);
  const save = async () => {
    try {
      if (cap !== rules.cap) await set.mutateAsync({ key: "season_tech_day_cap", value: cap });
      if (reserve !== rules.reserve) await set.mutateAsync({ key: "season_reserve_slots", value: reserve });
      if (miles !== rules.maxMiles) await set.mutateAsync({ key: "planner_max_miles", value: miles });
      onClose();
    } catch (e) { toastError(`Couldn't save the rules — ${(e as Error).message}`); }
  };
  return (
    <Modal open={open} onClose={onClose} size="sm"
      title={
        <span className="flex items-center gap-2">
          Planner Rules
          <InfoTip label="About the planner rules">What a filled tech-day looks like in season. Saved with the other plan settings.</InfoTip>
        </span>
      }
      footer={<div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">Cancel</button><button type="button" onClick={() => void save()} disabled={set.isPending} className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">Save</button></div>}>
      <div className="space-y-3 text-xs">
        <label className="flex items-center justify-between gap-3"><span className="text-slate-700"><b>Checks per tech-day</b><br /><span className="text-slate-500">in season — the cap Fill works to</span></span><input type="number" min={1} max={20} value={cap} onChange={(e) => setCap(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))} className="w-16 rounded-md border border-hairline px-2 py-1 text-right tabular-nums" /></label>
        <label className="flex items-center justify-between gap-3"><span className="text-slate-700"><b>Slots kept open</b><br /><span className="text-slate-500">for no-heat calls that come in</span></span><input type="number" min={0} max={8} value={reserve} onChange={(e) => setReserve(Math.max(0, Math.min(8, parseInt(e.target.value, 10) || 0)))} className="w-16 rounded-md border border-hairline px-2 py-1 text-right tabular-nums" /></label>
        <label className="flex items-center justify-between gap-3"><span className="text-slate-700"><b>How far Fill reaches</b><br /><span className="text-slate-500">miles from where the tech already is</span></span><input type="number" min={3} max={80} value={miles} onChange={(e) => setMiles(Math.max(3, Math.min(80, parseInt(e.target.value, 10) || 3)))} className="w-16 rounded-md border border-hairline px-2 py-1 text-right tabular-nums" /></label>
        <div className="rounded-md border border-hairline bg-slate-50/60 p-2.5">
          <div className="flex items-center gap-1.5 text-slate-700">
            <Home className="h-3.5 w-3.5 text-slate-500" /><b>Where an Open Day Starts</b>
            <InfoTip label="How the start point is chosen">A part pickup first thing starts the day at {rules.office?.label ?? "the office"}. Otherwise the day starts from home, as entered on the technician in ESC.</InfoTip>
          </div>
          {homes.length > 0 && (
            <ul className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
              {homes.map((h) => <li key={h.empno} className="flex justify-between gap-2"><span className="font-medium text-slate-700">{firstName(h.name)}</span><span className={cn("truncate", h.label ? "text-slate-500" : "text-amber-700")}>{h.label ?? "no address in ESC"}</span></li>)}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Direction B — the whole season on the map. Draw a loop (or pick a city) →
// the members inside are a batch → put it on a tech-day that has room, nearest
// first → Pegasus lays it out in drive order on that day.
// ============================================================================
interface Batch {
  kind: "loop" | "city";
  label: string;
  path: LatLng[] | null;
  members: PoolMember[];
}

function MapMode({ pool, poolLoading, rules, days, seasonLabel, checkLabel, onPlaced }: {
  pool: PoolMember[];
  poolLoading: boolean;
  rules: PlannerRules;
  days: VisitDay[];
  seasonLabel: string;
  checkLabel: string;
  onPlaced: (empno: string, day: string) => void;
}) {
  const today = isoDate(new Date());
  const horizon = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 13); return isoDate(d); }, []);
  const { data: cells } = useTechDayLoad(today, horizon);
  const createDay = useCreateDay();
  const addStops = useAddStops();
  const [drawing, setDrawing] = useState(false);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [choice, setChoice] = useState<string | null>(null); // `${empno}|${day}`
  const [busy, setBusy] = useState(false);

  const cityCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of pool) { const c = (p.city ?? "").trim() || "Unknown"; m.set(c, (m.get(c) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [pool]);

  const loopPlans = useMemo(() => days.filter((d) => d.origin === "loop" && d.status !== "done"), [days]);

  const onDrawEnd = (path: LatLng[]) => {
    setDrawing(false);
    const inside = pool.filter((p) => insideLoop(p, path.map((q) => [q.lat, q.lng] as [number, number])));
    if (inside.length === 0) { toastError("Nobody still owed inside that loop — try a wider one."); return; }
    const cities = new Map<string, number>();
    for (const p of inside) { const c = (p.city ?? "").trim() || "Unknown"; cities.set(c, (cities.get(c) ?? 0) + 1); }
    const label = [...cities.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2).map(([c]) => c).join(" / ");
    setBatch({ kind: "loop", label: label || "Loop", path, members: inside });
    setChoice(null);
  };
  const pickCity = (city: string) => {
    if (!city) { setBatch(null); return; }
    const members = pool.filter((p) => ((p.city ?? "").trim() || "Unknown") === city);
    setBatch({ kind: "city", label: city, path: null, members });
    setChoice(null);
  };

  const center = useMemo(() => (batch && batch.members.length > 0 ? centroid(batch.members) : null), [batch]);
  const perDay = Math.max(1, rules.cap - rules.reserve);

  // Tech-days with room in the next two weeks, nearest to the batch first.
  const options = useMemo(() => {
    if (!cells || !center) return [] as { key: string; cell: TechDayCell; room: number; dist: number | null; why: string }[];
    return cells
      .filter((c) => c.day >= today && !c.off && roomFor(c, rules) > 0)
      .map((c) => {
        const room = roomFor(c, rules);
        const an = dayAnchor(c, rules);
        const dist = an.at ? haversineMiles(an.at, center) : null;
        const away = dist != null ? ` · ${dist.toFixed(0)} mi away` : "";
        const why = c.calls > 0 ? `already in ${c.cities ?? "the area"} · ${c.calls} call${c.calls === 1 ? "" : "s"}${away}` : an.kind === "pickup" ? `open day · starts at ${an.label} (part pickup)${away}` : an.kind === "home" ? `open day · from home (${an.label})${away}` : "open day · nothing nearby yet";
        return { key: `${c.empno}|${c.day}`, cell: c, room, dist, why };
      })
      .sort((x, y) => {
        const dx = x.dist ?? 9999; const dy = y.dist ?? 9999;
        if (dx !== dy) return dx - dy;
        return x.cell.day.localeCompare(y.cell.day) || firstName(x.cell.tech_name).localeCompare(firstName(y.cell.tech_name));
      });
  }, [cells, center, rules, today]);
  const chosen = options.find((o) => o.key === choice) ?? options[0] ?? null;

  const place = async () => {
    if (!batch || !chosen || busy) return;
    setBusy(true);
    try {
      const { data: callsData, error } = await supabase.rpc("pegasus_tech_day_calls", { p_empno: chosen.cell.empno, p_date: chosen.cell.day });
      if (error) throw error;
      const calls = (callsData ?? []) as TechDayCall[];
      const anchor: LatLng | null = dayAnchor(chosen.cell, rules).at ?? center;
      const from = anchor ?? center!;
      const members = [...batch.members].sort((a, b) => haversineMiles(from, a) - haversineMiles(from, b)).slice(0, chosen.room);
      const first = firstName(chosen.cell.tech_name);
      const existing = days.find((d) => d.tech_empno === chosen.cell.empno && d.plan_date === chosen.cell.day && d.status !== "done") ?? null;
      const reason = `${batch.kind === "loop" ? "A loop you drew" : "The whole city"} — ${batch.label}, ${batch.members.length} still owed — placed on ${first} · ${fmtDay(chosen.cell.day)}${chosen.cell.calls > 0 ? ` because he's already in ${chosen.cell.cities}` : ""}.${batch.members.length > members.length ? ` ${batch.members.length - members.length} stay in the loop for another day.` : ""}`;
      let dayId = existing?.id ?? null;
      if (!dayId) {
        dayId = await createDay.mutateAsync({ title: `${first} · ${fmtDay(chosen.cell.day)} · ${batch.label}`, plan_date: chosen.cell.day, tech_empno: chosen.cell.empno, source_label: seasonLabel, origin: "loop", area: batch.path ? batch.path.map((q) => [q.lat, q.lng] as [number, number]) : null, anchor_lat: anchor?.lat ?? null, anchor_lng: anchor?.lng ?? null, reason });
      }
      await addStops.mutateAsync({ dayId, stops: stopsForDay(calls, members, anchor, chosen.cell.day, rules.office), startPosition: existing?.stops.length ?? 0 });
      setBatch(null);
      onPlaced(chosen.cell.empno, chosen.cell.day);
    } catch (e) {
      toastError(`Couldn't place the batch — ${(e as Error).message}`);
    } finally { setBusy(false); }
  };

  const mapPoints = useMemo((): MapPoint[] => {
    const inBatch = new Set((batch?.members ?? []).map((m) => m.key));
    return pool.map((p) => ({
      id: `pool:${p.key}`, lat: p.lat, lng: p.lng, label: p.name, sublabel: p.city ?? undefined,
      colorHex: inBatch.has(p.key) ? "#1d4ed8" : p.pickedDate ? "#d97706" : p.atRisk ? "#e11d48" : p.renewal ? "#7c3aed" : "#94a3b8", colorKey: "pool",
      hover: { title: p.name, chips: [{ text: p.pickedDate ? `asked for ${fmtDay(p.pickedDate, { weekday: "short", month: "short", day: "numeric" })}` : p.atRisk ? "at risk" : p.renewal ? "renewal at this visit" : "still owed" }], meta: [p.address, p.city].filter(Boolean).join(" · ") || undefined, note: p.context },
    }));
  }, [pool, batch]);
  const loops = useMemo(() => [
    ...loopPlans.filter((d) => d.area && d.area.length >= 3).map((d) => ({ id: d.id, path: (d.area as [number, number][]).map(([lat, lng]) => ({ lat, lng })), color: "#7c3aed" })),
    ...(batch?.path ? [{ id: "__batch", path: batch.path, color: "#1d4ed8", selected: true }] : []),
  ], [loopPlans, batch]);

  const nRenew = batch?.members.filter((m) => m.renewal).length ?? 0;
  const nRisk = batch?.members.filter((m) => m.atRisk).length ?? 0;
  const nPicked = batch?.members.filter((m) => m.pickedDate).length ?? 0;

  return (
    <div className="relative min-h-[720px] flex-1 overflow-hidden rounded-xl border border-hairline bg-slate-100" style={{ height: "calc(100vh - 290px)" }}>
      <DispatchMap
        points={mapPoints}
        colorForKey={() => "#94a3b8"}
        drawMode={drawing}
        onDrawEnd={onDrawEnd}
        loops={loops}
        onSelectLoop={(id) => { const d = loopPlans.find((x) => x.id === id); if (d?.tech_empno && d.plan_date) onPlaced(d.tech_empno, d.plan_date); }}
        viewStorageKey="pegasus.planner.season.map"
        className="rounded-xl"
      />
      {/* Toolbar */}
      <div className="absolute left-3 top-3 z-20 flex items-center gap-1.5 rounded-lg border border-hairline bg-white/95 p-1.5 shadow-e2 backdrop-blur">
        <button type="button" onClick={() => setDrawing((v) => !v)} className={cn("inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors", drawing ? "bg-brand-700 text-white" : "border border-hairline bg-white text-slate-700 hover:bg-slate-50")}>
          <Lasso className="h-3.5 w-3.5" /> {drawing ? "Drawing — drag a loop" : "Draw a loop"}
        </button>
        <span className="relative">
          <select value={batch?.kind === "city" ? batch.label : ""} onChange={(e) => pickCity(e.target.value)} className="appearance-none rounded-md border border-hairline bg-white py-1.5 pl-2.5 pr-7 text-xs font-medium text-slate-700" aria-label="Pick a city">
            <option value="">Pick a city…</option>
            {cityCounts.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        </span>
        <span className="ml-1 text-[11px] text-slate-500">{poolLoading ? "Loading the season…" : `${pool.length.toLocaleString()} still owed on the map`}</span>
        <InfoTip label="How map batches work">Draw a loop around a neighborhood, or pick a city. Everyone still owed inside becomes a batch you put on a tech-day.{checkLabel === "visits" ? "" : " Purple loops are batches already placed."}</InfoTip>
      </div>
      <div className="absolute left-3 top-14 z-20 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-hairline bg-white/95 px-2.5 py-1.5 text-[11px] text-slate-600 shadow-e1 backdrop-blur">
        <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-slate-400 align-middle" />still owed</span>
        <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-violet-600 align-middle" />renewal at this visit</span>
        <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-rose-600 align-middle" />at risk</span>
        <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-600 align-middle" />asked for a day</span>
        <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-brand-700 align-middle" />in your batch</span>
      </div>

      {/* Saved loops */}
      {loopPlans.length > 0 && (
        <div className="absolute bottom-3 left-3 z-20 w-80 rounded-lg border border-hairline bg-white/95 p-2.5 shadow-e2 backdrop-blur">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Batches
            <InfoTip label="About saved batches">Loops already placed on a tech-day. Click one to open that day.</InfoTip>
            <span className="tabular-nums text-slate-400">{loopPlans.length}</span>
          </div>
          <ul className="mt-1 divide-y divide-slate-100">
            {loopPlans.slice(0, 6).map((d) => {
              const booked = d.stops.filter((s) => s.status === "booked").length;
              return (
                <li key={d.id}>
                  <button type="button" onClick={() => d.tech_empno && d.plan_date && onPlaced(d.tech_empno, d.plan_date)} className="flex w-full items-center gap-2 py-1.5 text-left hover:bg-slate-50">
                    <span className="h-2 w-2 rounded-full bg-violet-600" />
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800">{d.title}</span>
                    <span className="text-[11px] tabular-nums text-slate-500">{d.stops.length} · <b className={booked > 0 ? "text-emerald-700" : ""}>{booked} booked</b></span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Batch panel */}
      {batch && (
        <div className="absolute right-3 top-3 z-20 w-[380px] max-h-[calc(100%-1.5rem)] overflow-y-auto rounded-xl border border-hairline bg-white shadow-e4">
          <div className="border-b border-hairline px-4 pb-3 pt-3">
            <div className="flex items-center gap-2">
              <div className="font-display text-base font-bold tracking-tight text-slate-900">{batch.kind === "loop" ? "Inside your loop" : batch.label}</div>
              <button type="button" onClick={() => setBatch(null)} className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button>
            </div>
            <div className="text-xs text-slate-500"><b className="tabular-nums text-slate-900">{batch.members.length} still owed</b> · {batch.label} · about {Math.max(1, Math.ceil(batch.members.length / perDay))} tech-day{Math.ceil(batch.members.length / perDay) === 1 ? "" : "s"}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {nRenew > 0 && <span className="rounded-full bg-violet-50 px-2 py-px text-[11px] font-semibold text-violet-700">{nRenew} renewal{nRenew === 1 ? "" : "s"}</span>}
              {nRisk > 0 && <span className="rounded-full bg-rose-50 px-2 py-px text-[11px] font-semibold text-rose-700">{nRisk} at risk</span>}
              {nPicked > 0 && <span className="rounded-full bg-amber-50 px-2 py-px text-[11px] font-semibold text-amber-700">{nPicked} asked for a day</span>}
            </div>
          </div>
          <ul className="max-h-56 overflow-y-auto px-4 py-1">
            {batch.members.slice(0, 40).map((m) => (
              <li key={m.key} className="flex items-center gap-2 border-t border-slate-50 py-1.5 first:border-t-0">
                <span className={cn("h-2 w-2 shrink-0 rounded-full", m.atRisk ? "bg-rose-600" : m.renewal ? "bg-violet-600" : m.pickedDate ? "bg-amber-600" : "bg-slate-400")} />
                <div className="min-w-0 flex-1"><div className="truncate text-xs font-semibold text-slate-800">{m.name}</div><div className="truncate text-[11px] text-slate-500">{[m.address, m.city].filter(Boolean).join(" · ")}</div></div>
                <button type="button" onClick={() => setBatch({ ...batch, members: batch.members.filter((x) => x.key !== m.key) })} className="rounded p-0.5 text-slate-300 hover:text-rose-500" aria-label="Leave out"><X className="h-3 w-3" /></button>
              </li>
            ))}
            {batch.members.length > 40 && <li className="py-1.5 text-center text-[11px] text-slate-400">+ {batch.members.length - 40} more</li>}
          </ul>
          <div className="border-t border-hairline bg-slate-50/70 px-4 pb-4 pt-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Put on a day</span>
              <InfoTip label="How placing a batch works">Tech-days with room are listed nearest first. Each member lands on that day's call list with the window already set. Booking is one tap when they answer; “No answer” keeps them on the list for tomorrow.</InfoTip>
            </div>
            {options.length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">No tech-day with room in the next two weeks.</p>
            ) : (
              <div className="mt-1.5 space-y-1.5">
                {options.slice(0, 5).map((o) => {
                  const on = chosen?.key === o.key;
                  return (
                    <button key={o.key} type="button" onClick={() => setChoice(o.key)} className={cn("flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors", on ? "border-brand-500 bg-brand-50" : "border-hairline bg-white hover:bg-slate-50")}>
                      <span className={cn("h-3.5 w-3.5 shrink-0 rounded-full border-2", on ? "border-brand-600 bg-brand-600 shadow-[inset_0_0_0_2px_#fff]" : "border-slate-300 bg-white")} />
                      <TechAvatar empno={o.cell.empno} name={o.cell.tech_name} size="xs" />
                      <div className="min-w-0 flex-1"><div className="text-xs font-semibold text-slate-800">{firstName(o.cell.tech_name)} · {fmtDay(o.cell.day)}</div><div className="truncate text-[11px] text-slate-500">{o.why}</div></div>
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-px text-[11px] font-semibold tabular-nums text-slate-600">room for {o.room}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {chosen && (
              <>
                <div className="mt-2.5 text-[11px] text-slate-600">
                  <b className="text-slate-800">{Math.min(chosen.room, batch.members.length)}</b> go on {firstName(chosen.cell.tech_name)} {fmtDay(chosen.cell.day, { weekday: "short" })}
                  {batch.members.length > chosen.room ? <> · <b className="text-slate-800">{batch.members.length - chosen.room}</b> stay in the {batch.kind === "loop" ? "loop" : "city"} for another day</> : null}
                </div>
                <button type="button" onClick={() => void place()} disabled={busy} className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-700 px-3 py-2 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300">
                  <RouteIcon className="h-3.5 w-3.5" /> {busy ? "Laying out…" : "Lay them out in drive order"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ============================================================================
// Direction C — Pegasus proposes, you approve. The 6:05 PM job (and "Propose
// now") builds the next ten working days to the pace; each proposal is a
// day plan in status 'proposed'. Approve books the members who asked for that
// day and keeps the rest on the call list; Open & adjust takes it to the week.
// ============================================================================
function ProposalsMode({ days, rules, perWeek, finishISO, seasonRows, onOpen, onRules }: {
  days: VisitDay[];
  rules: PlannerRules;
  perWeek: number;
  finishISO: string;
  seasonRows: SeasonVisit[];
  onOpen: (empno: string, day: string) => void;
  onRules: () => void;
}) {
  const propose = useProposeNow();
  const proposals = useMemo(() => days.filter((d) => d.status === "proposed" && d.plan_date && d.tech_empno).sort((a, b) => a.plan_date!.localeCompare(b.plan_date!) || a.title.localeCompare(b.title)), [days]);
  const byDate = useMemo(() => {
    const m = new Map<string, VisitDay[]>();
    for (const d of proposals) m.set(d.plan_date!, [...(m.get(d.plan_date!) ?? []), d]);
    return [...m.entries()];
  }, [proposals]);
  const [filter, setFilter] = useState<string | null>(null);
  const shown = filter ? byDate.filter(([d]) => d === filter) : byDate;
  const proposedStops = proposals.reduce((s, d) => s + d.stops.length, 0);
  const lastRun = proposals.reduce<string | null>((m, d) => (d.proposed_at && (!m || d.proposed_at > m) ? d.proposed_at : m), null);
  const waiting = useMemo(() => {
    const picked = seasonRows.filter((r) => r.status === "picked" && !r.placed_day_id).length;
    const call = seasonRows.filter((r) => isOutstanding(r.status) && (r.reason ?? "").startsWith("no text or email")).length;
    const snoozed = seasonRows.filter((r) => r.status === "snoozed").length;
    return { picked, call, snoozed };
  }, [seasonRows]);
  const perDay = Math.max(1, Math.ceil(perWeek / 5 / Math.max(1, rules.cap - rules.reserve)));

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[320px_minmax(0,1fr)] items-start gap-4">
      <div className="flex flex-col gap-3">
        <Card className="p-3.5">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Next ten working days</div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div><div className="font-display text-2xl font-bold leading-none tabular-nums text-slate-900">{proposedStops}</div><div className="text-[11px] text-slate-500">checks proposed</div></div>
            <div><div className="font-display text-2xl font-bold leading-none tabular-nums text-slate-900">{perWeek}</div><div className="text-[11px] text-slate-500">needed a week for {fmtDay(finishISO, { month: "short", day: "numeric" })}</div></div>
            <div><div className="font-display text-2xl font-bold leading-none tabular-nums text-slate-900">{proposals.length}</div><div className="text-[11px] text-slate-500">proposed tech-days</div></div>
            <div><div className="font-display text-2xl font-bold leading-none tabular-nums text-slate-900">{perDay}</div><div className="text-[11px] text-slate-500">tech-days a day to stay on pace</div></div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
            <Moon className="h-3.5 w-3.5 text-slate-400" /> {lastRun ? `Built ${new Date(lastRun).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}` : "Nothing proposed yet"} · runs weeknights at 6:05 PM
          </div>
          <button type="button" onClick={() => propose.mutate(10, { onError: (e) => toastError(`Couldn't propose — ${(e as Error).message}`) })} disabled={propose.isPending} className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-brand-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5" /> {propose.isPending ? "Proposing…" : "Propose now"}
          </button>
          {propose.data && <div className="mt-1.5 text-[11px] text-slate-500">Added {propose.data.proposed_days} tech-day{propose.data.proposed_days === 1 ? "" : "s"} · {propose.data.proposed_stops} checks{propose.data.proposed_days === 0 ? " — every tech-day that needs one already has a plan" : ""}.</div>}
        </Card>
        <Card className="p-3.5">
          <div className="flex items-center"><span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Rules Pegasus follows</span><button type="button" onClick={onRules} className="ml-auto text-[11px] font-semibold text-brand-700 hover:text-brand-900">Edit</button></div>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-700">
            <li className="flex gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /><span><b>{rules.cap} checks</b> per tech-day in season, <b>{rules.reserve} slot{rules.reserve === 1 ? "" : "s"}</b> kept open for no-heat calls</span></li>
            <li className="flex gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /><span>Start from what's <b>already on the Board</b> that day; never more than <b>~{rules.maxMiles} mi</b> from it</span></li>
            <li className="flex gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /><span>Only as many tech-days a day as the <b>finish line needs</b> — the rest stay open</span></li>
            <li className="flex gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /><span>Members who <b>asked for a day</b> online always land on that day; <b>renewals at risk</b> go first</span></li>
            <li className="flex gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /><span>Honors the <b>"requests / don't send"</b> watch-outs on the customer</span></li>
            <li className="flex gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /><span><b>Home areas</b>: {Object.keys(rules.homeAreas).length > 0 ? Object.values(rules.homeAreas).map((h) => h.label).join(" · ") : "none set — an open day starts from the city with the most still owed"}</span></li>
          </ul>
        </Card>
        <Card className="p-3.5">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Also waiting</div>
          <div className="mt-1.5 text-xs leading-relaxed text-slate-700"><b className="tabular-nums">{waiting.picked}</b> asked for a day nobody's proposed yet · <b className="tabular-nums">{waiting.call}</b> on the call list (no text or email) · <b className="tabular-nums">{waiting.snoozed}</b> snoozed</div>
        </Card>
      </div>

      <div className="min-w-0">
        {proposals.length === 0 ? (
          <Card className="flex min-h-[320px] flex-col items-center justify-center p-8 text-center">
            <Inbox className="h-9 w-9 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-700">Nothing proposed right now.</p>
            <p className="mt-1 max-w-md text-xs leading-relaxed text-slate-500">Pegasus proposes weeknights at 6:05 PM for the next ten working days — or hit <b>Propose now</b>.</p>
          </Card>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Proposals</span>
              <button type="button" onClick={() => setFilter(null)} className={cn("rounded-md border px-2 py-1 text-[11px] font-medium", !filter ? "border-brand-500 bg-brand-700 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>All <span className="tabular-nums opacity-70">· {proposals.length}</span></button>
              {byDate.map(([d, list]) => (
                <button key={d} type="button" onClick={() => setFilter(filter === d ? null : d)} className={cn("rounded-md border px-2 py-1 text-[11px] font-medium", filter === d ? "border-brand-500 bg-brand-700 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>{fmtDay(d, { weekday: "short", day: "numeric" })} <span className="tabular-nums opacity-70">· {list.length}</span></button>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              {shown.map(([d, list]) => list.map((p) => <ProposalCard key={p.id} plan={p} onOpen={() => onOpen(p.tech_empno!, d)} />))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProposalCard({ plan, onOpen }: { plan: VisitDay; onOpen: () => void }) {
  const view = usePlannerView();
  const { data: callsData } = useTechDayCalls(plan.tech_empno, plan.plan_date);
  const calls = useMemo(() => callsData ?? [], [callsData]);
  const { data: techs } = useActiveTechsLite();
  const techName = techs?.get(plan.tech_empno ?? "") ?? plan.tech_empno ?? "";
  const first = firstName(techName);
  const { data: rulesData } = usePlannerRules();
  const office = rulesData?.office ?? null;
  const anchor = useMemo(() => (typeof plan.anchor_lat === "number" && typeof plan.anchor_lng === "number" ? { lat: plan.anchor_lat, lng: plan.anchor_lng } : null), [plan.anchor_lat, plan.anchor_lng]);
  const { rows } = useMemo(() => buildRows(calls, plan.stops, anchor, office), [calls, plan.stops, anchor, office]);
  const pickup = rows.find((r) => r.pickup)?.pickup ?? null;
  const miles = useMemo(() => routeMiles(rows.map(rowPos)), [rows]);
  const decide = useDecideProposal();
  const bookStop = useBookStop();
  const reorder = useReorderStops();
  const [busy, setBusy] = useState(false);
  const existing = calls.filter((c) => !c.pseudo).length;
  const picked = plan.stops.filter((s) => s.priority === "picked" && s.status !== "booked");
  const cities = useMemo(() => {
    const seen: string[] = [];
    for (const r of rows) { const c = (r.call?.city ?? r.stop?.city ?? "").trim(); const cc = c ? c.toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase()) : ""; if (cc && !seen.includes(cc)) seen.push(cc); }
    return seen.slice(0, 4).join(" → ");
  }, [rows]);

  const persistWindows = async () => {
    const orderedIds: string[] = []; const windows: Record<string, { arrival_window: string; eta_hour: number }> = {};
    for (const r of rows) if (r.stop) { orderedIds.push(r.stop.id); if (r.stop.status !== "booked" && !r.stop.window_locked) windows[r.stop.id] = { arrival_window: r.seq.window, eta_hour: Math.round(r.seq.eta * 4) / 4 }; }
    if (orderedIds.length > 0) await reorder.mutateAsync({ orderedIds, windows });
  };
  const approve = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await persistWindows();
      await decide.mutateAsync({ dayId: plan.id, action: "approve" });
      for (const s of picked) {
        const win = rows.find((r) => r.stop?.id === s.id)?.seq.window ?? s.arrival_window ?? null;
        await bookStop.mutateAsync({ stop: s, dayTitle: plan.title, date: plan.plan_date!, window: win, tech: plan.tech_empno });
      }
    } catch (e) { toastError(`Couldn't approve — ${(e as Error).message}`); }
    finally { setBusy(false); }
  };
  const adjust = async () => {
    if (busy) return;
    setBusy(true);
    try { await persistWindows(); await decide.mutateAsync({ dayId: plan.id, action: "approve" }); onOpen(); }
    catch (e) { toastError(`Couldn't open — ${(e as Error).message}`); }
    finally { setBusy(false); }
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3 border-b border-hairline px-4 py-3">
        <TechAvatar empno={plan.tech_empno ?? ""} name={techName} size="md" />
        <div className="min-w-0">
          <div className="font-display text-[15px] font-bold tracking-tight text-slate-900">{first} · {fmtDay(plan.plan_date!, { weekday: "short", month: "short", day: "numeric" })} <span className="font-normal text-slate-500">· {cities || plan.title.split(" · ").slice(2).join(" · ")}</span></div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-slate-100 px-2 py-px text-[11px] font-semibold tabular-nums text-slate-600">{plan.stops.length} proposed{existing > 0 ? ` around ${existing} booked call${existing === 1 ? "" : "s"}` : " · open day"}</span>
            <span className="rounded-full bg-slate-100 px-2 py-px text-[11px] font-semibold tabular-nums text-slate-600">{rows.length} stops · ~{miles.toFixed(0)} mi</span>
            {picked.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-px text-[11px] font-semibold text-amber-700"><Hand className="h-3 w-3" /> {picked.length} asked for this day</span>}
            {pickup && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-px text-[11px] font-semibold text-amber-700"><Package className="h-3 w-3" /> {pickup.when === "start" ? `after a part pickup at ${pickup.label.replace(/^Part pickup · /, "")}` : "part pickup mid-day"}</span>}
            {rows.some((r) => r.seq.late) && <span className="rounded-full bg-rose-50 px-2 py-px text-[11px] font-semibold text-rose-700">a promised window is tight</span>}
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button type="button" onClick={() => void approve()} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 hover:bg-brand-800 disabled:bg-brand-300"><Check className="h-3.5 w-3.5" /> {busy ? "Working…" : picked.length > 0 ? `Approve · book the ${picked.length}` : "Approve day"}</button>
          <button type="button" onClick={() => void adjust()} disabled={busy} className="rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Open &amp; adjust</button>
          <button type="button" onClick={() => decide.mutate({ dayId: plan.id, action: "skip" })} disabled={busy || decide.isPending} className="rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50">Skip</button>
        </div>
      </div>
      <div className="flex items-stretch gap-1.5 overflow-x-auto px-4 py-2.5">
        {rows.map((r, i) => {
          const name = r.pickup ? "Part pickup" : r.call ? displayCustomerName(null, r.call.name) || r.call.custno : stopName(r.stop!);
          const kind = r.pickup ? "pickup" : r.call ? "lock" : r.stop!.status === "booked" ? "booked" : r.stop!.priority === "picked" ? "picked" : "prop";
          return (
            <div key={r.seq.id} className="flex items-center gap-1.5">
              <button type="button" onClick={() => (r.call ? view.openCall({ source: r.call.source, id: r.call.id }) : view.openCustomer(r.stop!.custno))} title={r.call ? "Open the ticket" : "Open the customer"} className={cn("flex min-w-[118px] max-w-[150px] flex-col overflow-hidden rounded-lg border bg-white text-left transition-shadow hover:shadow-e2", kind === "picked" ? "border-amber-300 ring-2 ring-amber-100" : "border-hairline")}>
                <div className={cn("px-2 py-0.5 text-[10px] font-bold tabular-nums tracking-wide text-white", kind === "pickup" ? "bg-amber-500" : kind === "lock" ? "bg-slate-700" : kind === "booked" ? "bg-emerald-600" : kind === "picked" ? "bg-amber-600" : "bg-brand-700")}>{r.pickup ? `${r.pickup.when === "start" ? "first" : r.seq.window} · pickup` : `${r.seq.window}${r.call ? " · booked" : ""}`}</div>
                <div className="px-2 py-1 leading-tight"><div className="truncate text-[11.5px] font-semibold text-slate-800">{name}</div><div className="truncate text-[10.5px] text-slate-500">{r.pickup ? r.pickup.label.replace(/^Part pickup · /, "") : (r.call?.city ?? r.stop?.city ?? "").trim() || "—"} · ~{fmtHour(r.seq.eta)}</div></div>
              </button>
              {i < rows.length - 1 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />}
            </div>
          );
        })}
      </div>
      {plan.reason && <div className="border-t border-hairline bg-slate-50/60 px-4 py-2 text-[11.5px] leading-relaxed text-slate-600"><Sparkles className="mr-1 inline h-3 w-3 text-brand-600 align-[-2px]" /><b className="text-slate-800">Why this day:</b> {plan.reason}</div>}
    </Card>
  );
}

/** Tech names by empno (small, cached) for the proposal cards. */
function useActiveTechsLite() {
  return useQuery({
    queryKey: ["active_techs_lite"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase.from("mirror_employee").select("empno, empname, firstname, lastname").eq("dept", "03").eq("inactive", false);
      if (error) throw error;
      return new Map(((data ?? []) as { empno: string; empname: string | null; firstname: string | null; lastname: string | null }[]).map((t) => [t.empno, (t.empname ?? "").trim() || `${t.firstname ?? ""} ${t.lastname ?? ""}`.trim()]));
    },
  });
}
